// src/App.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import ReactDOM from 'react-dom';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import Home from "./components/page/Home";
import PostDetail from "./components/page/PostDetail";
import LessonLibrary from "./components/page/LessonLibrary";
import LessonDetail from "./components/page/LessonDetail";
import MyClasses from "./components/page/MyClasses";
import Activities from "./components/page/Activities";
import Notifications from "./components/page/Notifications";
import LoginForm from "./components/page/LoginForm";
import Account from "./components/page/Account";
import Navbar from "./components/navbar/Navbar";
import AccountAdmin from "./components/page/admin/AccountAdmin";
import LessonAdmin from "./components/page/admin/LessonAdmin";
import ActivityAdmin from "./components/page/admin/ActivityAdmin";
import DashboardAdmin from "./components/page/admin/DashboardAdmin";
import SchoolYearsAdmin from "./components/page/admin/SchoolYearsAdmin";
import StudentHistory from "./components/page/admin/StudentHistory";
import TeacherHistory from "./components/page/admin/TeacherHistory";
import History from "./components/page/admin/History";
import RequireRole from './components/common/RequireRole';
import Toast from './components/common/Toast';
import AppScan from "./components/page/scan-app/AppScan";
import UpComming from "./components/page/UpComming";
import "./App.css";

const API_URL_FOR_FETCH_WRAPPER = process.env.REACT_APP_API_URL || '';
if (typeof window !== 'undefined' && API_URL_FOR_FETCH_WRAPPER) {
  try {
    const _origFetch = window.fetch.bind(window);
    window.fetch = async function (input, init) {
      try {
        let urlStr = null;
        if (typeof input === 'string') urlStr = input;
        else if (input && input.url) urlStr = input.url;

        const isApiCall = !!(urlStr && API_URL_FOR_FETCH_WRAPPER && urlStr.indexOf(API_URL_FOR_FETCH_WRAPPER) === 0);
        if (!isApiCall) return _origFetch(input, init);

        const headers = new Headers(init && init.headers ? init.headers : (input && input.headers ? input.headers : {}));
        if (!headers.has('ngrok-skip-browser-warning')) headers.set('ngrok-skip-browser-warning', 'true');

        const newInit = Object.assign({}, init, { headers });
        if (input instanceof Request) {
          const req = new Request(input, newInit);
          return _origFetch(req);
        }
        return _origFetch(input, newInit);
      } catch (e) {
        return _origFetch(input, init);
      }
    };
  } catch (e) {
    console.warn('Could not install global fetch wrapper for ngrok header', e);
  }
}

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export default function App() {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem('token'); } catch (e) { return null; }
  });

  const apiUrl = process.env.REACT_APP_API_URL;
  function normalizeUserUrl(src) {
    if (!src) return null;
    const s = String(src || '');
    if (s.startsWith('http://') || s.startsWith('https://')) return s;
    if (s.startsWith('/')) {
      const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
      return base + s;
    }
    if (!s.match(/^https?:\/\//)) {
      const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
      return base + '/' + s.replace(/^\//, '');
    }
    return s;
  }

  console.log('API URL:', apiUrl);

  useEffect(() => {
    // nếu có token thì fetch profile
    const tryProfile = async () => {
      if (!apiUrl || !token) return;
      try {
        const res = await fetchWithAuth(`${apiUrl}/api/auth/me`);
        if (!res.ok) {
          if (res.status !== 401) {
            setToken(null);
            setUser(null);
            try { localStorage.removeItem('token'); localStorage.removeItem('user'); } catch (e) { }
          }
          return;
        }
        const data = await res.json();
        const u = data.user || data;
        const safeUser = {
          id: u.id,
          username: u.username,
          role: u.role,
          student_ids: u.student_ids || [],
          school_id: u.school_id || null,
          school_name: u.school_name || null,
          class_id: u.class_id || null,
          full_name: u.full_name || null,
          first_name: u.first_name || null,
          last_name: u.last_name || null,
          identifier_code: u.identifier_code || null,
          avt: u.avt || null,
          email: u.email || null,
          parent_name: u.parent_name || null,
          status: u.status || null,
          isChangePass: Boolean(u.isChangePass)
        };
        setUser(safeUser);
        try { localStorage.setItem('user', JSON.stringify(safeUser)); } catch (e) { }
      } catch (e) {
        console.error('Failed to fetch profile', e);
      }
    };
    tryProfile();
    // chú: dependency token và apiUrl
  }, [apiUrl, token]);

  const login = async (username, password) => {
    if (!apiUrl) {
      alert('API not configured. Create a .env file in the project root with REACT_APP_API_URL=http://localhost:4000 and restart the dev server.');
      return;
    }
    if (apiUrl) {
      try {
        const res = await fetch(`${apiUrl}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || 'Login failed');
        }
        const data = await res.json();
        setToken(data.token);
        try { localStorage.setItem('token', data.token); } catch (e) { }

        // fetch full profile
        const meRes = await fetch(`${apiUrl}/api/auth/me`, { headers: { Authorization: `Bearer ${data.token}` } });
        if (meRes.ok) {
          const md = await meRes.json();
          const u = md.user || md;
          const safeUser = {
            id: u.id,
            username: u.username,
            role: u.role,
            student_ids: u.student_ids || [],
            school_id: u.school_id || null,
            school_name: u.school_name || null,
            class_id: u.class_id || null,
            full_name: u.full_name || null,
            identifier_code: u.identifier_code || null,
            avt: u.avt || null,
            email: u.email || null,
            parent_name: u.parent_name || null,
            status: u.status || null
          };
          // include isChangePass flag (ensure boolean)
          safeUser.isChangePass = Boolean(u.isChangePass);
          setUser(safeUser);
          try { localStorage.setItem('user', JSON.stringify(safeUser)); } catch (e) { }
          return safeUser; // return user so caller (LoginForm) can redirect
        }
        return null;
      } catch (e) {
        console.error('Login request failed', e);
        alert(e.message || 'Sai tài khoản hoặc mật khẩu');
        return;
      }
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    } catch (e) { }
  };

  const fetchWithAuth = async (url, opts = {}) => {
    const headers = Object.assign({}, opts.headers || {});
    if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      const res = await fetch(url, Object.assign({}, opts, { headers }));
      if (res.status === 401) {
        try { localStorage.removeItem('token'); localStorage.removeItem('user'); } catch (e) { }
        setUser(null);
        setToken(null);
        alert('Phiên bản hết hạn mời đăng nhập lại');
        return res;
      }
      return res;
    } catch (e) {
      throw e;
    }
  };

  // Refresh the current user profile from the server and update stored user
  const refreshUser = async () => {
    if (!apiUrl) return null;
    try {
      const res = await fetchWithAuth(`${apiUrl}/api/auth/me`);
      if (!res.ok) return null;
      const md = await res.json();
      const u = md.user || md;
      const safeUser = {
        id: u.id,
        username: u.username,
        role: u.role,
        student_ids: u.student_ids || [],
        school_id: u.school_id || null,
        school_name: u.school_name || null,
        class_id: u.class_id || null,
        full_name: u.full_name || null,
        identifier_code: u.identifier_code || null,
        avt: u.avt || null,
        email: u.email || null,
        parent_name: u.parent_name || null,
        status: u.status || null,
        isChangePass: Boolean(u.isChangePass)
      };
      setUser(safeUser);
      try { localStorage.setItem('user', JSON.stringify(safeUser)); } catch (e) { }
      return safeUser;
    } catch (e) { console.error('refreshUser failed', e); return null; }
  };

  // cung cấp context như trước
  const authContextValue = { user, login, logout, fetchWithAuth, normalizeUserUrl, apiUrl, refreshUser };

  return (
    <AuthContext.Provider value={authContextValue}>
      <Router>
        {/* AppInner contains Routes and uses router hooks safely */}
        <AppInner />
      </Router>
    </AuthContext.Provider>
  );
}

/**
 * AppInner:
 * - Được mount bên trong <Router> nên có thể dùng useNavigate/useLocation
 * - Chứa Navbar, header và Routes (giữ nguyên layout cũ)
 * - Khi `user` thay đổi, effect này sẽ redirect user mới login tới trang phù hợp
 */
function AppInner() {
  const { user, logout, normalizeUserUrl, apiUrl, fetchWithAuth, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 👇 Trang không có Navbar & Header + không dùng dashboard layout
  // Thêm các path khác nếu muốn ẩn layout ở nhiều trang
  const hideLayout = location.pathname === "/scan-app/scan";

  const [fpNext, setFpNext] = useState('');
  const [fpConfirm, setFpConfirm] = useState('');
  const [fpMsg, setFpMsg] = useState(null);
  const [fpSubmitting, setFpSubmitting] = useState(false);

  // Khi user change (vừa login), nếu đang ở /login hoặc / thì redirect theo role.
  useEffect(() => {
    if (!user) return;

    const path = location.pathname || '';
    // chỉ auto-redirect nếu đang ở trang login hoặc trang gốc
    if (path === '/login' || path === '/') {
      if (user.role === 'Admin') {
        navigate('/admin', { replace: true });
        return;
      }
      // Mods and other roles default to /
      navigate('/', { replace: true });
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className="app-container">
      {/* Ẩn Navbar khi ở /scan-app/scan */}
      {!hideLayout && <Navbar />}

      <Toast />

      <div
        className={
          hideLayout
            ? '' // không có layout -> để nội dung full width
            : `dashboard ${user ? 'has-top-header' : 'no-sidebar'}`
        }
        style={hideLayout ? { margin: 0, padding: 0, width: '100%', height: '100%' } : {}}
      >
        {/* Ẩn header khi ở /scan-app/scan */}
        {user && !hideLayout && (
          <>
            <div className="global-top-header">
              <div className="header-left">
                <div>
                  <span>Welcome Back, </span>
                  <span>
                    {user.first_name ?? "Unknown "}
                  </span>
                </div>
                <div>Let’s take a detailed look at today's lessons</div>
              </div>
              <div className="header-center"></div>
              <div className="header-right">
                <div className="h-bar-u-info">
                  <span>{(user.last_name || user.first_name) ? [user.last_name, user.first_name].filter(Boolean).join(' ') : (user.full_name || user.username)}</span>
                  <span>{user.role}</span>
                </div>
                <img
                  src={normalizeUserUrl(user && user.avt) || (apiUrl ? apiUrl.replace(/\/$/, '') + '/uploads/avt/avt-def.jpg' : '/uploads/avt/avt-def.jpg')}
                  alt="User Avatar"
                  height={30}
                  width={30}
                />
              </div>
            </div>

            <div className="global-mobile-header">
              <div className="mobile-header-left"></div>
              <div className="mobile-header-right">
                <span className="mobile-user-name">{(user.last_name || user.first_name) ? [user.last_name, user.first_name].filter(Boolean).join(' ') : (user.full_name || user.username)}</span>
                <img src={normalizeUserUrl(user && user.avt) || (apiUrl ? apiUrl.replace(/\/$/, '') + '/uploads/avt/avt-def.jpg' : '/uploads/avt/avt-def.jpg')} alt="avatar" className="mobile-header-avt" />
              </div>
            </div>
          </>
        )}

        <Routes>
          {!user ? (
            <>
              <Route path="/login" element={<LoginForm />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </>
          ) : (
            <>
              <Route path="/" element={<Home />} />
              <Route path="/lessons" element={<LessonLibrary />} />
              <Route path="/lessons/:lessonId/week/:week" element={<LessonDetail />} />
              <Route path="/classes" element={<MyClasses />} />
              <Route path="/activities" element={<Activities />} />
              <Route path="/scan-app/scan" element={<AppScan />} />
              <Route path="/help" element={<UpComming />} />
              <Route path="/training" element={<UpComming />} />
              <Route path="/settings" element={<UpComming />} />



              {/* Admin-only routes (RequireRole giữ nguyên) */}
              <Route
                path="/admin"
                element={
                  <RequireRole roles={["Admin", "Mod"]}>
                    {user && user.role === 'Admin' ? <DashboardAdmin /> : <Navigate to="/admin/accounts" />}
                  </RequireRole>
                }
              />
              <Route path="/admin/accounts" element={<RequireRole roles={["Admin", "Mod"]}><AccountAdmin /></RequireRole>} />
              <Route path="/admin/lessons" element={<RequireRole roles={["Admin"]}><LessonAdmin /></RequireRole>} />
              <Route path="/admin/school-years" element={<RequireRole roles={["Admin"]}><SchoolYearsAdmin /></RequireRole>} />
              <Route path="/admin/student-history" element={<RequireRole roles={["Admin", "Mod"]}><StudentHistory /></RequireRole>} />
              <Route path="/admin/teacher-history" element={<RequireRole roles={["Admin", "Mod"]}><TeacherHistory /></RequireRole>} />
              <Route path="/admin/history" element={<RequireRole roles={["Admin", "Mod"]}><History /></RequireRole>} />

              <Route path="/posts/:id" element={<PostDetail />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/profile" element={<Account />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </div>

      {/* Forced password change modal: show when user.isChangePass === true */}
      {user && user.isChangePass && ReactDOM.createPortal(
        <div className="confirm-modal" style={{ cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
          <div className="confirm-box" style={{ maxWidth: 520 }}>
            <h3>Change Password Required</h3>
            <p>Your account requires you to change your password before continuing. Please choose a new password.</p>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setFpMsg(null);
              if (!fpNext) return setFpMsg('Please enter a new password');
              if (fpNext !== fpConfirm) return setFpMsg('Passwords do not match');
              setFpSubmitting(true);
              try {
                const res = await fetchWithAuth(`${apiUrl}/api/auth/change-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ next: fpNext }) });
                if (!res.ok) {
                  const d = await res.json().catch(()=>({}));
                  throw new Error(d.message || 'Failed to change password');
                }
                // reload profile to pick up cleared isChangePass
                await refreshUser();
                setFpMsg('Password changed. You may continue.');
                setFpNext(''); setFpConfirm('');
              } catch (err) {
                console.error(err);
                setFpMsg(err.message || 'Failed to change password');
              } finally { setFpSubmitting(false); }
            }}>
              <div>
                <label>New password</label>
                <input type="password" value={fpNext} onChange={(e) => setFpNext(e.target.value)} />
              </div>
              <div>
                <label>Confirm password</label>
                <input type="password" value={fpConfirm} onChange={(e) => setFpConfirm(e.target.value)} />
              </div>
              <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>Password must be at least 8 characters long and include both letters and numbers.</div>
              <div style={{ marginTop: 12 }}>
                <button className="btn" type="submit" disabled={fpSubmitting}>{fpSubmitting ? 'Saving...' : 'Change Password'}</button>
                <button className="btn cancel" type="button" style={{ marginTop: 8 }} onClick={() => { logout(); navigate('/login'); }}>Logout</button>
              </div>
              {fpMsg && <div className="info" style={{ marginTop: 8 }}>{fpMsg}</div>}
            </form>
          </div>
        </div>,
        document.body
      )}
      {user && !hideLayout && <div className="overlay"></div>}
    </div>
  );
}
